import { Suspense, useState, useEffect } from 'react';
import { Skeleton } from '@mui/material';
import { Column } from 'further-ui/components/Layout';
import { CalendarDay } from 'further-ui/utils';
import api from 'lib/trpcClient';
import { useLayoutContext } from 'contexts/LayoutContext';
import { CurrencySettings } from '../../types';
import PcapDateSelector from './PcapDateSelector';
import PcapTable from './PcapTable';

type Props = {
  investorId: string;
  commitmentId?: string;
  vehicleId: string;
  currencySettings: CurrencySettings;
  investorName: string;
};

const PcapContent: React.FC<
  Props & { selectedDate: CalendarDay; vehicleName?: string }
> = ({
  investorId,
  commitmentId,
  vehicleId,
  currencySettings,
  investorName,
  vehicleName,
  selectedDate,
}) => {
  const [pcapData] = api.commitments.getPcap.useSuspenseQuery({
    vehicleId,
    investorId,
    ...(commitmentId ? { commitmentId } : {}),
    endDate: selectedDate,
  });

  return (
    <Column fullWidth>
      <PcapTable
        pcap={pcapData}
        currency={currencySettings.currencyLcy}
        investorName={investorName}
        vehicleName={vehicleName}
      />
    </Column>
  );
};

const PcapSection: React.FC<Props> = ({
  investorId,
  commitmentId,
  vehicleId,
  currencySettings,
  investorName,
}) => {
  const { vehicle } = useLayoutContext();
  const [selectedDate, setSelectedDate] = useState<CalendarDay | null>(null);

  const [periods] = api.accountingPeriod.listPeriods.useSuspenseQuery({
    vehicleId,
  });

  useEffect(() => {
    if (periods.length > 0 && !selectedDate) {
      setSelectedDate(periods[0].periodEnd);
    }
  }, [periods, selectedDate]);

  if (!selectedDate) return null;

  return (
    <Column spacing="md" fullWidth>
      <PcapDateSelector
        selectedDate={selectedDate}
        onChange={setSelectedDate}
      />
      <Suspense fallback={<Skeleton height={400} />}>
        <PcapContent
          investorId={investorId}
          commitmentId={commitmentId}
          vehicleId={vehicleId}
          currencySettings={currencySettings}
          investorName={investorName}
          vehicleName={vehicle?.vehicleName}
          selectedDate={selectedDate}
        />
      </Suspense>
    </Column>
  );
};

export default PcapSection;
